/**
 * @fileoverview Service for Meta Ads Connect API operations.
 * @module meta-ads-connect/services/MetaAdsConnectService
 */

import { getAuth } from 'firebase/auth';
import type {
  MetaAdsAdAccountsResponse,
  MetaAdsConnectionStatus,
  MetaAdsDeliverySyncRequest,
  MetaAdsDeliverySyncResponse,
  MetaAdsDisconnectResponse,
  MetaAdsInsightsPreviewRequest,
  MetaAdsInsightsPreviewResponse,
  MetaAdsMetadataPreviewRequest,
  MetaAdsMetadataPreviewResponse,
  MetaAdsMetadataSyncRequest,
  MetaAdsMetadataSyncResponse,
  MetaAdsOAuthUrlResponse,
  MetaAdsSelectAdAccountsResponse,
  MetaAdsUtmMapping,
  MetaAdsUtmMappingDetectResponse,
  MetaAdsUtmMappingResponse,
} from '../types';

function getApiUrl(): string {
  if (import.meta.env.VITE_AGENT_API_URL) {
    return import.meta.env.VITE_AGENT_API_URL;
  }
  if (import.meta.env.VITE_BUILDER_API_URL) {
    return import.meta.env.VITE_BUILDER_API_URL;
  }

  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8081';
  }
  if (hostname.includes('devsegmentlyai')) {
    return 'https://builder-service-519070724517.us-central1.run.app';
  }
  return 'https://api.segmently.ai';
}

export class MetaAdsConnectService {
  private readonly apiUrl: string;

  constructor(apiUrl?: string) {
    this.apiUrl = apiUrl || getApiUrl();
  }

  private async getAuthHeaders(): Promise<Record<string, string>> {
    const auth = getAuth();
    const token = await auth.currentUser?.getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }

    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  async getOAuthUrl(projectId: string, returnTo?: string): Promise<string> {
    const headers = await this.getAuthHeaders();
    const params = new URLSearchParams({ projectId });
    if (returnTo) {
      params.set('returnTo', returnTo);
    }

    const response = await fetch(`${this.apiUrl}/api/meta-ads/oauth/url?${params}`, {
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to get Meta Ads OAuth URL' }));
      throw new Error(error.error || 'Failed to get Meta Ads OAuth URL');
    }

    const data = await response.json() as MetaAdsOAuthUrlResponse;
    return data.url;
  }

  async getStatus(projectId: string, signal?: AbortSignal): Promise<MetaAdsConnectionStatus> {
    const headers = await this.getAuthHeaders();
    const params = new URLSearchParams({ projectId });
    const response = await fetch(`${this.apiUrl}/api/meta-ads/status?${params}`, {
      headers,
      signal,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to get Meta Ads connection status' }));
      throw new Error(error.error || 'Failed to get Meta Ads connection status');
    }

    return response.json();
  }

  async disconnect(projectId: string): Promise<MetaAdsDisconnectResponse> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.apiUrl}/api/meta-ads/disconnect`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ projectId }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to disconnect Meta Ads' }));
      throw new Error(error.error || 'Failed to disconnect Meta Ads');
    }

    return response.json();
  }

  /**
   * G1 (AC-G1-1): persist the explicit ad-account selection. The server
   * validates against a live account re-discovery (typed 403 naming any
   * account the token cannot see) and reconciles sync tasks inline.
   */
  async selectAdAccounts(
    projectId: string,
    adAccountIds: string[],
  ): Promise<MetaAdsSelectAdAccountsResponse> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(
      `${this.apiUrl}/api/projects/${encodeURIComponent(projectId)}/integrations/meta-ads/accounts/select`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ adAccountIds }),
      },
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to save Meta Ads account selection' }));
      throw new Error(error.error || 'Failed to save Meta Ads account selection');
    }

    return response.json();
  }

  async listAdAccounts(projectId: string): Promise<MetaAdsAdAccountsResponse> {
    const headers = await this.getAuthHeaders();
    const params = new URLSearchParams({ projectId });
    const response = await fetch(`${this.apiUrl}/api/meta-ads/ad-accounts?${params}`, {
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to list Meta Ads ad accounts' }));
      throw new Error(error.error || 'Failed to list Meta Ads ad accounts');
    }

    return response.json();
  }

  async getInsightsPreview(
    request: MetaAdsInsightsPreviewRequest,
  ): Promise<MetaAdsInsightsPreviewResponse> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.apiUrl}/api/meta-ads/insights-preview`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to preview Meta Ads insights' }));
      throw new Error(error.error || 'Failed to preview Meta Ads insights');
    }

    return response.json();
  }

  async getMetadataPreview(
    request: MetaAdsMetadataPreviewRequest,
  ): Promise<MetaAdsMetadataPreviewResponse> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.apiUrl}/api/meta-ads/metadata-preview`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to preview Meta Ads metadata' }));
      throw new Error(error.error || 'Failed to preview Meta Ads metadata');
    }

    return response.json();
  }

  async syncDelivery(
    request: MetaAdsDeliverySyncRequest,
  ): Promise<MetaAdsDeliverySyncResponse> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.apiUrl}/api/meta-ads/sync-delivery`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to sync Meta Ads delivery' }));
      throw new Error(error.error || 'Failed to sync Meta Ads delivery');
    }

    return response.json();
  }

  async syncMetadata(
    request: MetaAdsMetadataSyncRequest,
  ): Promise<MetaAdsMetadataSyncResponse> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.apiUrl}/api/meta-ads/sync-metadata`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to sync Meta Ads metadata' }));
      throw new Error(error.error || 'Failed to sync Meta Ads metadata');
    }

    return response.json();
  }

  /** Deterministic session-based autodetect of the project's UTM convention. */
  async detectUtmMapping(projectId: string, adAccountId?: string): Promise<MetaAdsUtmMappingDetectResponse> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.apiUrl}/api/meta-ads/utm-mapping/detect`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ projectId, ...(adAccountId ? { adAccountId } : {}) }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to detect UTM mapping' }));
      throw new Error(error.error || 'Failed to detect UTM mapping');
    }

    return response.json();
  }

  /** Confirm (or clear with null) the project-declared UTM mapping. */
  async setUtmMapping(projectId: string, mapping: MetaAdsUtmMapping | null): Promise<MetaAdsUtmMappingResponse> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.apiUrl}/api/meta-ads/utm-mapping`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ projectId, mapping }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to save UTM mapping' }));
      throw new Error(error.error || 'Failed to save UTM mapping');
    }

    return response.json();
  }
}

let singleton: MetaAdsConnectService | null = null;

export function getMetaAdsConnectService(): MetaAdsConnectService {
  if (!singleton) {
    singleton = new MetaAdsConnectService();
  }
  return singleton;
}

export function createMetaAdsConnectService(apiUrl?: string): MetaAdsConnectService {
  return new MetaAdsConnectService(apiUrl);
}

export default MetaAdsConnectService;
